import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrud } from '../lib/useCrud';
import { formatDateTime } from '../lib/formatUtils';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Edit, Trash2, MoreVertical, Users, Search, Shield, UserCheck, UserX, KeyRound, Wand2, Eye, EyeOff, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { PersonAvatar } from '../components/PersonAvatar';
import { PasswordStrengthHint } from '../components/PasswordStrengthHint';
import { checkPasswordStrength, PASSWORD_POLICY_MESSAGE } from '../utils/passwordStrength';

const API = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { first_name: '', last_name: '', email: '', role_id: '', status: 'active', password: '' };

function generateStrongPassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const symbols = '@#$%&*!?';
  const all = upper + lower + digits + symbols;
  let pw = '';
  pw += upper[Math.floor(Math.random() * upper.length)];
  pw += lower[Math.floor(Math.random() * lower.length)];
  pw += digits[Math.floor(Math.random() * digits.length)];
  pw += symbols[Math.floor(Math.random() * symbols.length)];
  for (let i = 4; i < 14; i++) pw += all[Math.floor(Math.random() * all.length)];
  return pw.split('').sort(() => Math.random() - 0.5).join('');
}

export const UsersPage = () => {
  const { data: users, loading, fetchData, create, update, remove } = useCrud('/users');
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { user: currentUser, token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [roles, setRoles] = useState([]);

  // Fetch roles dynamically
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get('/roles');
        if (res.data.success) setRoles(res.data.data);
      } catch { /* role fetch is best-effort */ }
    };
    fetchRoles();
  }, []);

  // Reset password modal state
  const [isResetPwOpen, setIsResetPwOpen] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetSendEmail, setResetSendEmail] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const filtered = users.filter(u => {
    if (searchQuery && !`${u.first_name} ${u.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) && !u.email?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedUser) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        delete payload.send_email;
        if (selectedUser.id === currentUser?.id && payload.status === 'inactive') {
          toast.error('You cannot deactivate your own account');
          return;
        }
        await update(selectedUser.id, payload);
        toast.success('User updated');
      } else {
        if (!checkPasswordStrength(formData.password).valid) {
          toast.error(PASSWORD_POLICY_MESSAGE);
          return;
        }
        const payload = { ...formData, send_email: sendEmail };
        const res = await create(payload);
        if (sendEmail && res?.email_sent) {
          toast.success('User created and password sent via email');
        } else if (sendEmail && !res?.email_sent) {
          toast.success('User created, but email could not be sent. Check SMTP settings.');
        } else {
          toast.success('User created');
        }
      }
      fetchData();
    } catch { toast.error('Operation failed'); }
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    const confirmed = await confirm({ title: 'Delete User', description: `Are you sure you want to delete user ${u.first_name} ${u.last_name}? The account will be soft-deleted and can be restored later.` });
    if (!confirmed) return;
    try { await remove(u.id); toast.success('User deleted'); fetchData(); } catch { toast.error('Delete failed'); }
  };

  const handleToggleStatus = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    const isActive = u.status === 'active';
    const action = isActive ? 'Deactivate' : 'Activate';
    const confirmed = await confirm({
      title: `${action} User`,
      description: isActive
        ? `Are you sure you want to deactivate ${u.first_name} ${u.last_name}? They will no longer be able to log in.`
        : `Are you sure you want to reactivate ${u.first_name} ${u.last_name}? They will be able to log in again.`,
      confirmText: action,
      variant: isActive ? 'destructive' : 'default',
    });
    if (!confirmed) return;
    try {
      await update(u.id, { ...u, status: isActive ? 'inactive' : 'active' });
      toast.success(`${u.first_name} ${u.last_name} ${isActive ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch { toast.error('Status update failed'); }
  };

  const handleEdit = (u) => {
    setSelectedUser(u);
    setFormData({ first_name: u.first_name || '', last_name: u.last_name || '', email: u.email || '', role_id: u.role_id || '', status: u.status || 'active', password: '' });
    setIsEditing(true);
    setShowPassword(false);
    setSendEmail(false);
    setIsDialogOpen(true);
  };

  const handleOpenResetPw = (u) => {
    setResetPwUser(u);
    setResetPwValue('');
    setShowResetPw(false);
    setResetSendEmail(false);
    setIsResetPwOpen(true);
  };

  const handleResetPwSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!checkPasswordStrength(resetPwValue).valid) {
      toast.error(PASSWORD_POLICY_MESSAGE);
      return;
    }
    setResetLoading(true);
    try {
      const res = await fetch(`${API}/api/users/${resetPwUser.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: resetPwValue, send_email: resetSendEmail }),
      });
      const json = await res.json();
      if (json.success) {
        if (resetSendEmail && json.email_sent) {
          toast.success('Password reset and sent via email');
        } else if (resetSendEmail && !json.email_sent) {
          toast.success('Password reset, but email could not be sent. Check SMTP settings.');
        } else {
          toast.success('Password reset successfully');
        }
        setIsResetPwOpen(false);
      } else {
        toast.error(json.detail || json.message || 'Reset failed');
      }
    } catch {
      toast.error('Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  }, [resetPwUser, resetPwValue, resetSendEmail, token]);

  const resetForm = () => { setFormData(emptyForm); setSelectedUser(null); setIsEditing(false); setShowPassword(false); setSendEmail(false); };

  const getStatusBadge = (s) => {
    const cfg = { active: 'bg-emerald-500/10 text-emerald-500', inactive: 'bg-gray-500/10 text-gray-400' };
    return <Badge className={cfg[s] || cfg.active}>{s}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">User Management</h1><p className="text-muted-foreground">Manage system users and access</p></div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { if (!o) { setIsDialogOpen(false); resetForm(); } }}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-user-btn" onClick={() => { resetForm(); setIsDialogOpen(true); }}><Plus className="h-4 w-4 mr-2" />Add User</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit User' : 'Add User'}</DialogTitle><DialogDescription>{isEditing ? 'Update user account details' : 'Create a new user account'}</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>First Name *</Label><Input value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} required data-testid="user-first-name" /></div>
                <div className="space-y-2"><Label>Last Name *</Label><Input value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required data-testid="user-email" /></div>
                <div className="space-y-2"><Label>Role</Label><Select value={formData.role_id} onValueChange={v => setFormData({ ...formData, role_id: v })}><SelectTrigger data-testid="user-role"><SelectValue placeholder="Select role" /></SelectTrigger><SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select></div>
                {!isEditing && (
                  <div className="space-y-2 col-span-2">
                    <div className="flex items-center justify-between">
                      <Label>Password *</Label>
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => { const pw = generateStrongPassword(); setFormData({ ...formData, password: pw }); setShowPassword(true); }} data-testid="generate-password-btn">
                        <Wand2 className="h-3 w-3" />Auto Generate
                      </Button>
                    </div>
                    <div className="relative">
                      <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required={!isEditing} className="pr-10" data-testid="user-password" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <PasswordStrengthHint password={formData.password} />
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox id="send-email-create" checked={sendEmail} onCheckedChange={setSendEmail} data-testid="send-email-checkbox" />
                      <label htmlFor="send-email-create" className="text-sm text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                        <Mail className="h-3.5 w-3.5" />Send password to user via email
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Cancel</Button><Button type="submit" data-testid="user-submit-btn">{isEditing ? 'Update' : 'Create'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPwOpen} onOpenChange={(o) => { if (!o) setIsResetPwOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" />Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {resetPwUser?.first_name} {resetPwUser?.last_name} ({resetPwUser?.email})</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPwSubmit} className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>New Password *</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => { const pw = generateStrongPassword(); setResetPwValue(pw); setShowResetPw(true); }} data-testid="reset-generate-password-btn">
                  <Wand2 className="h-3 w-3" />Auto Generate
                </Button>
              </div>
              <div className="relative">
                <Input type={showResetPw ? 'text' : 'password'} value={resetPwValue} onChange={e => setResetPwValue(e.target.value)} required minLength={8} className="pr-10" data-testid="reset-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowResetPw(!showResetPw)} tabIndex={-1}>
                  {showResetPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordStrengthHint password={resetPwValue} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="send-email-reset" checked={resetSendEmail} onCheckedChange={setResetSendEmail} data-testid="reset-send-email-checkbox" />
              <label htmlFor="send-email-reset" className="text-sm text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                <Mail className="h-3.5 w-3.5" />Send new password to user via email
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsResetPwOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={resetLoading} data-testid="reset-password-submit-btn">{resetLoading ? 'Resetting...' : 'Reset Password'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card><CardContent className="py-4"><div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="user-search" /></div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold">{users.length}</p><p className="text-xs text-muted-foreground">Total Users</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Users className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{users.filter(u => u.status === 'active').length}</p><p className="text-xs text-muted-foreground">Active</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><Shield className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold">{new Set(users.map(u => u.role)).size}</p><p className="text-xs text-muted-foreground">Roles</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        <Table data-testid="users-table"><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Last Login</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{filtered.map(u => (
          <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
            <TableCell>
              {u.is_employee && u.employee_id ? (
                <button
                  type="button"
                  onClick={() => navigate(`/employees/${u.employee_id}`)}
                  className="flex items-center gap-3 text-left rounded-md -mx-1 px-1 py-0.5 transition-colors hover:bg-muted/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary group"
                  title="Open employee profile"
                  data-testid={`open-employee-${u.id}`}
                >
                  <PersonAvatar firstName={u.first_name} lastName={u.last_name} picture={u.profile_picture} seed={u.id} size="md" />
                  <p className="font-medium group-hover:text-primary group-hover:underline">{u.first_name} {u.last_name}</p>
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <PersonAvatar firstName={u.first_name} lastName={u.last_name} picture={u.profile_picture} seed={u.id} size="md" />
                  <p className="font-medium">{u.first_name} {u.last_name}</p>
                </div>
              )}
            </TableCell>
            <TableCell><a href={`mailto:${u.email}`} className="text-primary hover:underline text-sm">{u.email}</a></TableCell>
            <TableCell><Badge variant="outline">{u.role_name || 'N/A'}</Badge></TableCell>
            <TableCell><Badge className={u.is_employee ? 'bg-blue-500/10 text-blue-500' : 'bg-violet-500/10 text-violet-500'}>{u.is_employee ? 'Employee' : 'System User'}</Badge></TableCell>
            <TableCell>{getStatusBadge(u.status)}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{formatDateTime(u.last_login) || 'Never'}</TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleOpenResetPw(u)} data-testid={`reset-pw-${u.id}`}><KeyRound className="h-4 w-4 mr-2" />Reset Password</DropdownMenuItem>
                <DropdownMenuSeparator />
                {u.status === 'active'
                  ? <DropdownMenuItem onClick={() => handleToggleStatus(u)}><UserX className="h-4 w-4 mr-2" />Deactivate</DropdownMenuItem>
                  : <DropdownMenuItem onClick={() => handleToggleStatus(u)}><UserCheck className="h-4 w-4 mr-2" />Activate</DropdownMenuItem>
                }
                <DropdownMenuItem onClick={() => handleDelete(u)} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
};
